import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { brandIColors } from '../../lib/theme';

/**
 * Branded wordmark: "peopleiknow" (all lowercase) in Comfortaa (rounded,
 * geometric). "people"/"know" render in the surface's own high-contrast text
 * colour; the signature lowercase "i" is a LARGER Comfortaa glyph in a
 * non-black theme accent.
 *
 * Alignment: the "i" is baseline-aligned with the rest of the word (its foot
 * sits on the same baseline) — it is NOT raised. It's simply a bigger font,
 * so its stem rises to ~the ascender height of the "k"/"l" and its dot floats
 * above, matching the brand reference.
 *
 * Accent colour: chosen ONCE per mount and re-rolled only on route change —
 * never animated/continuous. It's derived to stay high-contrast: PikBrand
 * measures its own rendered text colour and picks the deep pool on light
 * backgrounds / the bright pool on dark ones, so even inline usages (no props)
 * self-correct across themes.
 */
export default function PikBrand({
  size = 'inherit',
  className = '',
  testId = 'pik-brand',
  color,
}) {
  const location = useLocation();
  const ref = useRef(null);
  const [onDark, setOnDark] = useState(false);
  // Fresh accent per mount AND on every navigation — but stable while a page
  // is being viewed (no continuous cycling).
  const [seed, setSeed] = useState(() => Math.random());
  useEffect(() => {
    setSeed(Math.random());
  }, [location.pathname]);

  // Derive light/dark from the wordmark's ACTUAL rendered text colour so the
  // accent always contrasts the real background (handles `currentColor`).
  useLayoutEffect(() => {
    if (!ref.current) return;
    const rgb = getComputedStyle(ref.current).color || '';
    const m = rgb.match(/[\d.]+/g);
    if (!m || m.length < 3) return;
    const [r, g, b] = m.slice(0, 3).map((v) => Number(v) / 255);
    const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    setOnDark(lum > 0.5); // light text ⇒ dark background
  }, [color, location.pathname]);

  const pool = brandIColors(onDark);
  const accent = pool[Math.floor(seed * pool.length) % pool.length];
  const fontSize = size === 'inherit' ? undefined : size;

  return (
    <span
      ref={ref}
      className={`pik-brand ${className}`.trim()}
      data-testid={testId}
      style={{
        fontSize,
        color: color || 'inherit',
        fontFamily: "'Comfortaa', system-ui, -apple-system, sans-serif",
        fontWeight: 600,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
      }}
    >
      <span>people</span>
      <span
        style={{
          fontSize: '1.4em',
          fontWeight: 700,
          color: accent,
          margin: '0 0.008em',
        }}
        aria-hidden="true"
      >
        i
      </span>
      <span>know</span>
    </span>
  );
}
